/**
 * Sound Notification Utility
 * Plays different sounds for different e-commerce events
 */

// Sound notification types
export type NotificationSound = 'cart-add' | 'order-placed' | 'cart-abandoned' | 'new-customer';

// Audio Context for generating sounds
let audioContext: AudioContext | null = null;

const getAudioContext = (): AudioContext => {
  if (!audioContext) {
    audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  return audioContext;
};

/**
 * Play a notification sound
 * @param type Type of notification sound to play
 * @param volume Volume level (0-1), defaults to 0.3
 */
export const playNotificationSound = (type: NotificationSound, volume: number = 0.3) => {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    
    switch (type) {
      case 'cart-add':
        // Pleasant "ding" sound for cart additions
        playCartAddSound(ctx, now, volume);
        break;
      
      case 'order-placed':
        // Celebratory "success" sound for orders
        playOrderPlacedSound(ctx, now, volume);
        break;
      
      case 'cart-abandoned':
        // Subtle "notification" sound for abandoned carts
        playCartAbandonedSound(ctx, now, volume);
        break;
      
      case 'new-customer':
        // Welcoming "chime" for new customers
        playNewCustomerSound(ctx, now, volume);
        break;
    }
  } catch (error) {
    console.warn('Failed to play notification sound:', error);
  }
};

/**
 * Cart Add Sound - Pleasant single chime (C5 note)
 */
const playCartAddSound = (ctx: AudioContext, startTime: number, volume: number) => {
  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();
  
  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);
  
  oscillator.frequency.setValueAtTime(523.25, startTime); // C5
  gainNode.gain.setValueAtTime(volume, startTime);
  gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + 0.3);
  
  oscillator.start(startTime);
  oscillator.stop(startTime + 0.3);
};

/**
 * Order Placed Sound - Celebratory ascending chime (C5 -> E5 -> G5)
 */
const playOrderPlacedSound = (ctx: AudioContext, startTime: number, volume: number) => {
  const notes = [
    { freq: 523.25, time: 0 },     // C5
    { freq: 659.25, time: 0.1 },   // E5
    { freq: 783.99, time: 0.2 },   // G5
  ];
  
  notes.forEach(note => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(note.freq, startTime + note.time);
    gainNode.gain.setValueAtTime(volume, startTime + note.time);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + note.time + 0.2);
    
    oscillator.start(startTime + note.time);
    oscillator.stop(startTime + note.time + 0.2);
  });
};

/**
 * Cart Abandoned Sound - Gentle descending notification (G5 -> E5)
 */
const playCartAbandonedSound = (ctx: AudioContext, startTime: number, volume: number) => {
  const notes = [
    { freq: 783.99, time: 0 },     // G5
    { freq: 659.25, time: 0.1 },   // E5
  ];
  
  notes.forEach(note => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(note.freq, startTime + note.time);
    gainNode.gain.setValueAtTime(volume * 0.5, startTime + note.time); // Quieter
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + note.time + 0.25);
    
    oscillator.start(startTime + note.time);
    oscillator.stop(startTime + note.time + 0.25);
  });
};

/**
 * New Customer Sound - Welcoming double chime (E5 -> G5)
 */
const playNewCustomerSound = (ctx: AudioContext, startTime: number, volume: number) => {
  const notes = [
    { freq: 659.25, time: 0 },     // E5
    { freq: 783.99, time: 0.12 },  // G5
  ];
  
  notes.forEach(note => {
    const oscillator = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    oscillator.connect(gainNode);
    gainNode.connect(ctx.destination);
    
    oscillator.frequency.setValueAtTime(note.freq, startTime + note.time);
    gainNode.gain.setValueAtTime(volume * 0.7, startTime + note.time);
    gainNode.gain.exponentialRampToValueAtTime(0.001, startTime + note.time + 0.25);
    
    oscillator.start(startTime + note.time);
    oscillator.stop(startTime + note.time + 0.25);
  });
};

/**
 * Test all notification sounds
 */
export const testNotificationSounds = () => {
  console.log('🔊 Testing notification sounds...');
  
  setTimeout(() => {
    console.log('🛒 Cart Add');
    playNotificationSound('cart-add');
  }, 0);
  
  setTimeout(() => {
    console.log('✅ Order Placed');
    playNotificationSound('order-placed');
  }, 1000);
  
  setTimeout(() => {
    console.log('⚠️ Cart Abandoned');
    playNotificationSound('cart-abandoned');
  }, 2500);
  
  setTimeout(() => {
    console.log('👋 New Customer');
    playNotificationSound('new-customer');
  }, 4000);
};

